import React, { PureComponent } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { cloneDeep } from 'lodash/fp';
import moment from 'moment';
import uniqid from 'uniqid';
import faker from 'faker';
import PropTypes from 'prop-types';

class TrendingChart extends PureComponent {
  constructor(props) {
    super(props);
    this.ascendingGradient = uniqid();
    this.descendingGradient = uniqid();
    this.end = Math.round(new Date().getTime() / 1000); // in seconds
    this.start = Math.round(moment(this.end * 1000).add(-3, 'months').toDate().getTime() / 1000); // in seconds
    this.state = {
      series: this.generateSeries(this.start, this.end, 3600 * 4) // per 4 hours
    };
  }

  generateSeries(start, end, step) {
    const result = [];
    for (let i = start; i < end; i += step) {
      result.push({
        time: i,
        value: faker.datatype.number({ min: 100, max: 900 })
      });
    }
    return result;
  }

  handleSeries() {
    const result = [];
    const series = cloneDeep(this.state.series);
    for (let i = 0; i < series.length; i++) {
      let found = false;
      switch (this.props.duration) {
        case 'day':
          found = series[i].time >= this.end - 86400;
          break;
        case 'week':
          found = series[i].time >= this.end - 86400 * 7;
          break;
        case 'month':
          const start = Math.round(moment(this.end * 1000).add(-1, 'month').toDate().getTime() / 1000);
          found = series[i].time >= start;
          break;
        case 'all':
          found = true;
          break;
        default:
          found = false;
          break;
      }
      if (found) {
        result.push(series[i]);
      }
    }

    let directions = [];
    if (result.length > 1) {
      // find out whether 1st point is trending up or down
      let startIndex = 0;
      let direction = -1;
      for (let i = 0; i < result.length - 1; i++) {
        if (result[i].value < result[i + 1].value) { // trending up
          startIndex = i;
          direction = 0;
          break;
        } else if (result[i].value > result[i + 1].value) { // trending down
          startIndex = i;
          direction = 1;
          break;
        }
      }
      if (direction === -1) { // all points has the same value
        // do nothing
        // use original value to draw single series
      } else { // this data series has inflection
        // save immutable values to 1st series
        for (let i = 0; i < startIndex; i++) {
          result[i].value0 = result[i].value;
        }
        // iterate over inflection points
        for (let i = startIndex; i < result.length - 1; i++) {
          if (result[i].value < result[i + 1].value) { // trending up
            if (i === startIndex) { // detect the direction of immutable values
              directions.push(0);
            }
            result[i][`value${directions.length - 1}`] = result[i].value;
            if (direction !== 0) {
              result[i][`value${directions.length}`] = result[i].value;
              directions.push(0);
              direction = 0;
            }
          } else if (result[i].value > result[i + 1].value) { // treding down
            if (i === startIndex) { // detect the direction of immutable values
              directions.push(1);
            }
            result[i][`value${directions.length - 1}`] = result[i].value;
            if (direction !== 1) {
              result[i][`value${directions.length}`] = result[i].value;
              directions.push(1);
              direction = 1;
            }
          } else { // holding
            result[i][`value${directions.length - 1}`] = result[i].value;
          }
        }
        // save the last point
        result[result.length - 1][`value${directions.length - 1}`] = result[result.length - 1].value;
      }
    }
    return { result, directions };
  }

  render() {
    const { result, directions } = this.handleSeries();
    const fields = [];
    for (let i = 0; i < directions.length; i++) {
      fields.push(`value${i}`);
    }
    return (
      <ResponsiveContainer height={this.props.height}>
        <AreaChart data={result}>
          <defs>
            <linearGradient id={this.ascendingGradient} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9AC802" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#9AC802" stopOpacity={0} />
            </linearGradient>
            <linearGradient id={this.descendingGradient} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FA4F4F" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#FA4F4F" stopOpacity={0} />
            </linearGradient>
          </defs>
          {directions.length === 0 ? (
            <Area
              type="monotone"
              dataKey="value"
              stroke="#9AC802"
              fill={`url(#${this.ascendingGradient})`}
            />
          ) : fields.map((field, index) => (
            <Area
              key={index}
              type="monotone"
              dataKey={field}
              stroke={directions[index] % 2 === 0 ? '#9AC802' : '#FA4F4F'}
              fill={directions[index] % 2 === 0 ? `url(#${this.ascendingGradient})` : `url(#${this.descendingGradient})`}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }
}

TrendingChart.propTypes = {
  height: PropTypes.number.isRequired,
  duration: PropTypes.string.isRequired
};

export default TrendingChart;
